import {
  ButtonHTMLAttributes,
  FC,
  ReactElement,
  SVGProps,
  memo,
  useCallback,
} from "react";
import { TypeOptions } from "react-toastify";
import {
  ToastErrorIcon,
  ToastSaveIcon,
  ToastSuccessIcon,
  ToastWarningIcon,
} from "~@/icons";
import { sanitize } from "~@/utils";

type CustomTypeOptions =
  | Extract<TypeOptions, "success" | "error" | "warning">
  | "save";

type ToastContentProps = {
  message: string;
} & (
  | {
      type?: CustomTypeOptions;
      icon?: undefined;
    }
  | {
      type?: undefined;
      icon?: () => ReactElement<SVGProps<SVGSVGElement>>;
    }
) &
  (
    | {
        hasButton?: true;
        button: (props: {
          className: string;
        }) => ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
      }
    | {
        hasButton?: false;
        button?: undefined;
      }
  );

const ToastIcon: Readonly<{
  [type in CustomTypeOptions]: ReactElement<SVGProps<SVGSVGElement>>;
}> = {
  success: <ToastSuccessIcon />,
  warning: <ToastWarningIcon />,
  error: <ToastErrorIcon />,
  save: <ToastSaveIcon />,
};

const ToastContent: FC<ToastContentProps> = ({
  message,
  type,
  icon,
  hasButton,
  button,
}) => {
  const renderIcon = useCallback(() => {
    return type ? (
      <div className="icon">{ToastIcon[type]}</div>
    ) : icon ? (
      <div className="icon">{icon()}</div>
    ) : null;
  }, [type, icon]);

  const renderButton = useCallback(() => {
    if (!hasButton) return null;

    return button({ className: "toast-content__button" });
  }, [hasButton, button]);

  return (
    <div className="toast-content">
      <div className="icon-text icon-text--has-space-around icon-text--has-gap-8">
        {renderIcon()}
        <span>
          {sanitize({
            html: message,
            options: {
              allowedTags: ["b"],
            },
          })}
        </span>
      </div>
      {renderButton()}
    </div>
  );
};

export default memo(ToastContent);
